import { withJWTClient } from "../connectClient";

import {
  ListPropagationsResponseSchema,
  PropagationSchema,
  UpdatePropagationItemResponseSchema,
  type CreatePropagationRequest,
  type ListPropagationsResponse,
  type Propagation,
  type PropagationItem,
  type UpdatePropagationItemRequest,
  type UpdatePropagationRequest,
} from "@/types/propagation";

const API_BASE_URL: string = import.meta.env.VITE_CREATIVESPHERE_API_URL || "";

/** POST /v1/{site_id}/propagations — creates & initializes a propagation. */
export async function createPropagation(
  siteId: string,
  req: CreatePropagationRequest,
): Promise<Propagation> {
  const response = await withJWTClient((client) =>
    client.post<unknown>(`${API_BASE_URL}/v1/${siteId}/propagations`, req),
  );
  return PropagationSchema.parse(response.data);
}

/** GET /v1/{site_id}/propagations/{slug} — full current propagation state. */
export async function getPropagation(
  siteId: string,
  slug: string,
): Promise<Propagation> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/v1/${siteId}/propagations/${slug}`),
  );
  return PropagationSchema.parse(response.data);
}

export interface ListPropagationsParams {
  status?: string;
  limit?: number;
  offset?: number;
}

/** GET /v1/{site_id}/propagations — paginated list, optionally by status. */
export async function listPropagations(
  siteId: string,
  params: ListPropagationsParams = {},
): Promise<ListPropagationsResponse> {
  const response = await withJWTClient((client) =>
    client.get<unknown>(`${API_BASE_URL}/v1/${siteId}/propagations`, {
      params,
    }),
  );
  return ListPropagationsResponseSchema.parse(response.data);
}

/** PATCH /v1/{site_id}/propagations/{slug} — partial update with a field mask. */
export async function updatePropagation(
  siteId: string,
  slug: string,
  req: UpdatePropagationRequest,
): Promise<Propagation> {
  const response = await withJWTClient((client) =>
    client.patch<unknown>(
      `${API_BASE_URL}/v1/${siteId}/propagations/${slug}`,
      req,
    ),
  );
  return PropagationSchema.parse(response.data);
}

/** DELETE /v1/{site_id}/propagations/{slug}. */
export async function deletePropagation(
  siteId: string,
  slug: string,
): Promise<void> {
  await withJWTClient((client) =>
    client.delete<unknown>(`${API_BASE_URL}/v1/${siteId}/propagations/${slug}`),
  );
}

/**
 * PATCH /v1/{site_id}/propagations/{slug}/items/{item_id} — flips a single
 * item's acceptance. Unwraps the `{ item }` envelope.
 */
export async function updatePropagationItem(
  siteId: string,
  slug: string,
  itemId: string,
  req: UpdatePropagationItemRequest,
): Promise<PropagationItem> {
  const response = await withJWTClient((client) =>
    client.patch<unknown>(
      `${API_BASE_URL}/v1/${siteId}/propagations/${slug}/items/${itemId}`,
      req,
    ),
  );
  return UpdatePropagationItemResponseSchema.parse(response.data).item;
}
